import { onUnmounted } from 'vue'

export interface ViewportRestore {
  preserveViewportAfterFocus: (focus?: () => void) => void
  cancelViewportRestore: () => void
}

/**
 * Headless UI's teleported option list can scroll a long page when it closes (or when it calls
 * `scrollIntoView` on the selected option). Captures the viewport at the moment of a change and
 * reapplies it across two animation frames after an optional focus callback runs — used by
 * `Select`/`MultiSelect` so picking an option cannot jump a long report page to the top.
 */
export function useViewportRestore(): ViewportRestore {
  let generation = 0
  let timer: number | null = null
  const frames = new Set<number>()
  let disposed = false

  function cancelViewportRestore(): void {
    generation += 1
    if (timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }
    frames.forEach((frame) => window.cancelAnimationFrame(frame))
    frames.clear()
  }

  function preserveViewportAfterFocus(focus?: () => void): void {
    cancelViewportRestore()
    if (disposed) return

    const thisGeneration = generation
    const left = window.scrollX
    const top = window.scrollY
    const isCurrent = (): boolean => !disposed && thisGeneration === generation
    const restore = (remainingFrames: number): void => {
      if (!isCurrent()) return
      window.scrollTo(left, top)
      if (remainingFrames > 0) {
        const frame = window.requestAnimationFrame(() => {
          frames.delete(frame)
          restore(remainingFrames - 1)
        })
        frames.add(frame)
      }
    }
    const started = window.setTimeout(() => {
      if (timer === started) timer = null
      if (!isCurrent()) return
      focus?.()
      restore(2)
    }, 0)
    timer = started
  }

  onUnmounted(() => {
    disposed = true
    cancelViewportRestore()
  })

  return { preserveViewportAfterFocus, cancelViewportRestore }
}
